import { cast, flow, getSnapshot, SnapshotIn, toGenerator, types } from 'mobx-state-tree'

import { FetchModel } from '../models/FetchModel'
import {
  ExistingDiscrepancyReport,
  ExistingDiscrepancyReportEditCloneModel,
  ExistingDiscrepancyReportModel,
  NewDiscrepancyReportEditCloneModel,
  DiscrepancyReportEditCloneModel,
} from '../models/DiscrepancyReports/DiscrepancyReportsModel'
import {
  OperationFailure,
  OperationInProgress,
  OperationNotStarted,
  OperationStatusModel,
  OperationSuccess,
} from '../models/common/OperationStatus'
import { FilterModel } from '../models/common/Filters/FilterModel'
import { FilterType } from '../models/common/Filters/FilterType'
import { SelectFilterIcon, SelectFieldFilter } from '../models/common/Filters/SelectFieldFilter'
import type { SearchFieldFilter } from '../models/common/Filters/SearchFieldFilterModel'
import type { SelectOptions } from '../components/Select/Select'

export const STATUS = {
  Resolved : 'Resolved',
  Unresolved : 'Unresolved',
}

export const StatusOptions: SelectOptions = Object.entries(STATUS).map(
  ([status, displayStatus]) => ({ value: status, text: displayStatus }),
)

const DISCREPANCY_REPORT_PAGE_FILTERS: SnapshotIn<typeof FilterModel> = {
  fieldFilters: {
    stringSearch: { type: FilterType.SEARCH, placeholder: 'Search' },
    userSearch: { type: FilterType.SELECT, icon: SelectFilterIcon.User},
    status: { type: FilterType.SELECT, icon: SelectFilterIcon.Status, options: StatusOptions}, // TODO: change the icon
  },
}


export const DiscrepancyReportsStore = FetchModel.named('DiscrepancyReportsStore')
  .props({
    discrepancyReports: types.maybeNull(types.array(ExistingDiscrepancyReportModel)),

    selectedDiscrepancyReport: types.safeReference(ExistingDiscrepancyReportModel),
    discrepancyReportCopyForEditing: types.maybeNull(DiscrepancyReportEditCloneModel),

    filters: types.optional(FilterModel, DISCREPANCY_REPORT_PAGE_FILTERS),

    fetchStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    fetchFilterOptionsStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    saveStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    resolveStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),

    validationStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
  })
  .views((self) => ({
    get getDiscrepancyReportsUrl(): string {
      return `${self.baseUrl}discrepancy_reports/`
    },
    discrepancyReportUrl(discrepancyReportId: number): string {
      return `${self.baseUrl}discrepancy_reports/${discrepancyReportId}/`
    },
    resolveDiscrepancyReportUrl(discrepancyReportId: number): string {
      return `${self.baseUrl}discrepancy_reports/${discrepancyReportId}/resolve/`
    },
    _getFilterByFieldName(fieldName: string) {
      const filter = self.filters.fieldFilters.get(fieldName)
      if (filter === undefined) {
        throw new Error(`filter ${fieldName} must appear`)
      }
      return filter
    },
  }))
  .views((self) => ({
    get searchFilter(): SearchFieldFilter {
      return self._getFilterByFieldName('stringSearch') as SearchFieldFilter
    },
    get userFilter(): SelectFieldFilter {
      return self._getFilterByFieldName('userSearch') as SelectFieldFilter
    },
    get statusFilter(): SelectFieldFilter {
      return self._getFilterByFieldName('status') as SelectFieldFilter
    },
  }))
  .views((self) => ({
    get shownDiscrepancyReports(): Array<ExistingDiscrepancyReport> | null {
      if (self.discrepancyReports === null) return null
      const searchValue = self.searchFilter.value?.toLowerCase() ?? ''
      const userSearch = self.userFilter.filterValue?.value ?? ''
      const statusFilter = self.statusFilter.filterValue?.value ?? ''
      return self.discrepancyReports
        // search filter:
        .filter((discrepancyReport) => 
        (searchValue === '') || ( // in case it is an empty string, we will skip the filtering
        discrepancyReport.vehicle_id.toString().toLowerCase().includes(searchValue) || 
        discrepancyReport.vehicle_name?.toLowerCase().includes(searchValue) ||
        discrepancyReport.created_by?.name?.toLowerCase().includes(searchValue) || 
        discrepancyReport.resolved_by?.name?.toLowerCase().includes(searchValue) || 
        discrepancyReport.description?.toLowerCase().includes(searchValue)
        ))
        // users filter: 
        .filter((discrepancyReport) => 
        (userSearch === '') || (  // in case it is an empty string, we will skip the filtering
        discrepancyReport.created_by?.name.includes(userSearch) ||
        discrepancyReport.resolved_by?.name.includes(userSearch)))
        // status filter:
        .filter((discrepancyReport) => 
        (statusFilter === '') || (
        (statusFilter === STATUS.Resolved) === discrepancyReport.is_resolved 
        ))
    },
  }))

  .actions((self) => ({
    _setDiscrepancyReports(discrepancyReports: SnapshotIn<typeof self.discrepancyReports>) {
      self.discrepancyReports = cast(discrepancyReports)

      // selectedDiscrepancyReport is a safeReference, so it will automatically change to undefined if the corresponding discrepancy report is not in the array.
      // However, we must refresh discrepancyReportCopyForEditing manually.
      if (self.discrepancyReportCopyForEditing !== null && self.selectedDiscrepancyReport === undefined) {
        self.discrepancyReportCopyForEditing = null
      }
      const allUsers = new Set(self.discrepancyReports?.map(report => [report.created_by?.name, report.resolved_by?.name]).flat().filter(x=> x).map(String))
      self.userFilter.setOptions([...allUsers].map(user => ({ value: user, text: user })))
    },
  }))
  .actions((self) => ({
    clearSelectedDiscrepancyReport() {
      self.selectedDiscrepancyReport = undefined
      self.discrepancyReportCopyForEditing = null
    },
    trySetSelectedDiscrepancyReport(discrepancyReportId: number): boolean {
      if (self.discrepancyReports === null) {
        console.warn("Can't select discrepancy report when no discrepancy report fetched")
        return false
      }

      const selectedDiscrepancyReport = self.discrepancyReports.find((discrepancyReport) => discrepancyReport.id === discrepancyReportId)
      if (selectedDiscrepancyReport === undefined) {
        self.selectedDiscrepancyReport = undefined
        self.discrepancyReportCopyForEditing = null
        return false
      }

      self.selectedDiscrepancyReport = selectedDiscrepancyReport
      self.discrepancyReportCopyForEditing = DiscrepancyReportEditCloneModel.create(getSnapshot(selectedDiscrepancyReport))

      return true
    },
    createNewDiscrepancyReport() {
      if (self.discrepancyReports === null) {
        console.error("Can't create discrepancy report when no discrepancy reports fetched")
        throw new Error("Can't create discrepancy report when no discrepancy report fetched")
      }
      self.validationStatus = OperationNotStarted()
      
      self.selectedDiscrepancyReport = undefined
      self.discrepancyReportCopyForEditing = NewDiscrepancyReportEditCloneModel.create({
        vehicle_id: 0,
        vehicle_name: '',
        created_by: { name: '' },
        is_resolved: false,
        creation_timestamp: Date.now().toString(),
        resolved_by: null,
        resolution_timestamp: '',
        description: '',
      })
      
    },
  }))

  .views((self) => ({
    get isAddingDiscrepancyReport() {
      return NewDiscrepancyReportEditCloneModel.is(self.discrepancyReportCopyForEditing)
    },
  }))
  .actions((self) => ({
    fetchAndSetDiscrepancyReports: flow(function* fetchAndSetDiscrepancyReports() {
      self.fetchStatus = OperationInProgress()

      const loadResult = yield* toGenerator(
        self.loadObjectOrError<SnapshotIn<typeof self.discrepancyReports>>(
          self.getDiscrepancyReportsUrl,
          {},
          { fetchOperationName: 'Fetch reports' },
        ),
      )
      if (loadResult.error) {
        self.fetchStatus = OperationFailure(loadResult.error)
      } else {
        self._setDiscrepancyReports(loadResult.result)
        self.fetchStatus = OperationSuccess()
      }
    }),
  }))
  .actions((self) => ({
    resolveDiscrepancyReport: flow(function* resolvedDiscrepancyReport(reportId: number) {
      // If reportId is the id of the currently edited report, we take the editing clone as the report data we send to backend.
      // Otherwise, we take it from the reports array
      const report = 
        (ExistingDiscrepancyReportEditCloneModel.is(self.discrepancyReportCopyForEditing) && 
        reportId === self.discrepancyReportCopyForEditing.id) ? 
          self.discrepancyReportCopyForEditing :
          self.discrepancyReports?.find(report => report.id === reportId)
      if (!report) {
        throw new Error(`Discrepancy report ${reportId} not found`)
      }
      
      self.resolveStatus = OperationInProgress()
      const discrepancyReportJson = JSON.stringify(getSnapshot(report))
      
      const fetchResult = yield* toGenerator(
        self.loadObjectOrError<void>(
          self.resolveDiscrepancyReportUrl(reportId),
          { method: 'POST', body: discrepancyReportJson }, 
          { fetchOperationName: 'Resolve discrepancy report' }),
      )

      if (fetchResult.error) {
        self.resolveStatus = OperationFailure(fetchResult.error)
      } else {
        self.resolveStatus = OperationSuccess()
        void self
          .fetchAndSetDiscrepancyReports() // refresh discrepancy reports list
          .then(() => {
            if (
              self.selectedDiscrepancyReport !== undefined &&
              ExistingDiscrepancyReportEditCloneModel.is(self.discrepancyReportCopyForEditing) &&
              self.selectedDiscrepancyReport.id === self.discrepancyReportCopyForEditing.id
            ) {
              self.trySetSelectedDiscrepancyReport(self.selectedDiscrepancyReport.id)
            }
          })
        }
    },
    )}))
  .actions((self) => ({
    resolveEditedDiscrepancyReport: flow(function* resolvedDiscrepancyReport() {
      if (self.selectedDiscrepancyReport === undefined) {
        console.warn('No discrepancy report selected, cannot resolve')
        throw new Error('No discrepancy report selected, cannot resolve it')
      }
      yield self.resolveDiscrepancyReport(self.selectedDiscrepancyReport.id)
    },
    )}))
  .actions((self) => ({
    saveEditedDiscrepancyReport: flow(function* saveEditedDiscrepancyReport() {
      if (self.discrepancyReportCopyForEditing === null) {
        console.warn('No discrepancy report edited, cannot save')
        throw new Error('No discrepancy report edited, cannot save it')
      }
      self.validationStatus = OperationSuccess()
      self.saveStatus = OperationInProgress()
      const discrepancyReportJson = JSON.stringify(getSnapshot(self.discrepancyReportCopyForEditing))

      const [url, method] = ExistingDiscrepancyReportEditCloneModel.is(self.discrepancyReportCopyForEditing)
        ? [self.discrepancyReportUrl(self.discrepancyReportCopyForEditing.id), 'PATCH']
        : [self.getDiscrepancyReportsUrl, 'POST']

      const fetchResult = yield* toGenerator(
        self.loadObjectOrError<void>(url, { method, body: discrepancyReportJson }, { fetchOperationName: 'Save discrepancy report' }),
      )
      if (fetchResult.error) {
        self.saveStatus = OperationFailure(fetchResult.error)
      } else {
        self.saveStatus = OperationSuccess()
        void self
          .fetchAndSetDiscrepancyReports() // refresh discrepancy reports list
          .then(() => {
            if (
              self.selectedDiscrepancyReport !== undefined &&
              ExistingDiscrepancyReportEditCloneModel.is(self.discrepancyReportCopyForEditing) &&
              self.selectedDiscrepancyReport.id === self.discrepancyReportCopyForEditing.id
            ) {
              self.trySetSelectedDiscrepancyReport(self.selectedDiscrepancyReport.id)
            }
          })
      }
    }),
  }))
